import Button from '@mui/material/Button'
import useConditionalEffect from 'hooks/useConditionalEffect'
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { resendVerificationCode, validateVerificationCode } from 'services/checkout/actions'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import { MS_IN_SEC, SEC_IN_MIN } from 'utils/constants'
import { setAccount } from '../state/actions'
import { SignupContext } from '../state/context'
import styles from './VerifyEmail.pcss'

const CODE_LENGTH = 6
const TIMER_VALUE_EXPIRED = '0:00'

const formatTime = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / MS_IN_SEC))
  const minutes = Math.floor(totalSeconds / SEC_IN_MIN)
  const seconds = totalSeconds % SEC_IN_MIN
  return `${minutes.toString()}:${seconds.toString().padStart(2, '0')}`
}

export function VerifyEmail() {
  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const location = useLocation()
  const locationState = location.state as { ott: string } | undefined
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [{ account }, signupDispatch] = useContext(SignupContext)
  const [timer, setTimer] = useState<string | null>(null)

  const accountData = useMemo(() => {
    if (account) {
      return account
    }


    if (locationState?.ott) {
      return { loginOtt: locationState.ott }
    }

    if (localStorage) {
      const stored = localStorage.getItem('pp_signup_account')
      if (stored) {
        return JSON.parse(stored)
      }
    }

    return null
  }, [account, locationState])

  const resendCode = useCallback(({ loginOtt }: any) => {
    const token = loginOtt || accountData?.loginOtt
    if (token) {
      dispatch(resendVerificationCode(token))
        .subscribe((response: any) => {
          if (response.codeExpTime) {
            const updatedAccountData = { ...accountData, codeExpTime: response.codeExpTime }
            signupDispatch(setAccount(updatedAccountData))
            if (localStorage) {
              localStorage.setItem('pp_signup_account', JSON.stringify(updatedAccountData))
            }
            dispatch(message('A new verification code has been sent to your email.', 'success'))
          } else {
            dispatch(message('An error occurred while resending the code. Please try again.', 'error'))
          }
        }, (error: any) => {
          console.error('Error resending code', error)
          const msg = error.response?.error?.message
          dispatch(message(msg || 'An error occurred while resending the code. Please try again.', 'error'))
        })
    } else {
      dispatch(message(intl.formatMessage({ id: 'errors.generic-support' }), 'error'))
    }
  }, [accountData, dispatch, intl, signupDispatch])

  useConditionalEffect(([resend]) => {
    if (locationState?.ott) {
      resend({ loginOtt: locationState.ott })
    }
  }, [locationState?.ott], [resendCode])

  useLayoutEffect(() => {
    if (typeof accountData?.codeExpTime !== 'string') {
      return
    }

    const timeRemaining = new Date(accountData.codeExpTime).getTime() - Date.now()
    setTimer(formatTime(timeRemaining))

    const interval = setInterval(() => {
      const newTimeRemaining = new Date(accountData.codeExpTime).getTime() - Date.now()
      if (newTimeRemaining <= 0) {
        setTimer(TIMER_VALUE_EXPIRED)
        clearInterval(interval)
      } else {
        setTimer(formatTime(newTimeRemaining))
      }
    }, MS_IN_SEC)

    return () => {
      clearInterval(interval)
    }
  }, [accountData?.codeExpTime])

  const submit = () => {
    if (code.length === CODE_LENGTH) {
      setLoading(true)
      dispatch(validateVerificationCode(accountData.loginOtt, code))
        .subscribe((response: any) => {
          if (response.success) {
            localStorage?.removeItem('pp_signup_account')
            navigate(`/login?ott=${accountData.loginOtt}&redirectUrl=${encodeURIComponent('/onboarding')}`)
          } else {
            dispatch(message('The code you entered is incorrect. Please check your email and try again.', 'error'))
          }
          setLoading(false)
        }, error => {
          const errorCode = error.response?.error?.code
          let msg = ''
          switch (errorCode) {
            case 'wrong-security-code':
              msg = 'The code you entered is incorrect. Please check your email and try again.'
              break
            case 'security-code-expired':
              msg = 'This verification code has expired. Please request a new code.'
              break
            default:
              msg = error.response?.error?.message || 'An error occurred while verifying the code. Please try again.'
          }
          console.log('Error validating code', error)
          dispatch(message(msg, 'error'))
          setLoading(false)
        })
    } else {
      dispatch(message('Please enter the complete verification code.', 'error'))
    }
  }

  if (!accountData) {
    return <Navigate to="/signup/create-account" />
  }

  return (
    <div>
      <div className={styles.layout}>
        <h1 className={styles.title}>Verification Code</h1>
        <p className={styles.subtitle}>
          Please check your email
        </p>
        <div>
          <CodeInput length={CODE_LENGTH} onChange={setCode} />
        </div>
        {timer && (
          <div className={`${styles.timerLabel} ${timer === TIMER_VALUE_EXPIRED ? styles.err : ''}`}>
            <span className={styles.timer}>{timer}</span>
            {` remaining`}
          </div>
        )}
        <div className={styles.actions}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            className={styles['btn-submit']}
            disabled={loading}
            data-testid="btn-create-account"
            onClick={submit}
          >
            Verify
          </Button>
          <div className={styles.textActionWrapper}>
            <span className={styles.textAction} onClick={resendCode}>
              Didn't receive? Resend
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

const CodeInput = ({ length = CODE_LENGTH, onChange }: { length?: number, onChange?: (value: string) => void }) => {
  const [values, setValues] = useState(Array(length).fill(''))
  const inputsRef = useRef<HTMLInputElement[]>([])

  useEffect(() => {
    if (onChange) {
      onChange(values.join(''))
    }
  }, [values, onChange])

  const focusInput = (index: number) => {
    if (inputsRef.current[index]) {
      inputsRef.current[index].focus()
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const val = e.target.value
    if (!val) return

    const newValues = [...values]
    newValues[index] = val[0] // Only take the first char
    setValues(newValues)

    // Move to next input
    if (index < length - 1) {
      focusInput(index + 1)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      const newValues = [...values]
      if (values[index]) {
        newValues[index] = ''
        setValues(newValues)
      } else if (index > 0) {
        focusInput(index - 1)
      }
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData('text').slice(0, length)
    if (!paste) {
      return
    }

    const newValues = paste.split('').slice(0, length)
    setValues(() => newValues.concat(Array(length - newValues.length).fill('')))

    // Focus the next empty field
    const nextIndex = newValues.length < length ? newValues.length : length - 1
    focusInput(nextIndex)
    e.preventDefault()
  }

  return (
    <div className={styles.codeInput}>
      {values.map((val, index) => (
        <input
          key={index}
          type="text"
          maxLength={1}
          value={val}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          ref={(el) => {
            if (el) {
              inputsRef.current[index] = el
            }
          }}
          style={{
            width: '40px',
            height: '40px',
            textAlign: 'center',
            fontSize: '20px'
          }}
        />
      ))}
    </div>
  )
}

export default CodeInput
